import * as Sentry from '@sentry/react';
import { createContext, ReactNode, useMemo } from 'react';
import { logger } from '@tankef/env';
import type {
  TelemetryProvider,
  TelemetryConfig,
  LogLevel,
  LogContext,
  AnalyticsEvent,
  Span,
} from './index';
import type { AnalyticsAdapter } from './adapters/index';
import { sanitizeSentryEvent } from './sanitize';

// ═══════════════════════════════════════════════════════════════
// IMPLEMENTACIÓN WEB
// ═══════════════════════════════════════════════════════════════

export class WebTelemetryProvider implements TelemetryProvider {
  private config: TelemetryConfig;
  private adapters: AnalyticsAdapter[];
  private initialized = false;

  /**
   * @param config    - Sentry DSN, environment, etc.
   * @param adapters  - Destinos de analytics (GTM, Amplitude, etc.)
   *
   * Para agregar GA4 via GTM:
   *   new WebTelemetryProvider(config, [new GtmAdapter({ containerId: 'GTM-XXX' })])
   */
  constructor(config: TelemetryConfig, adapters: AnalyticsAdapter[] = []) {
    this.config = config;
    this.adapters = adapters;
    this.init();
    adapters.forEach(adapter => adapter.init?.());
  }

  private init(): void {
    if (this.initialized || !this.config.sentry.dsn) return;

    Sentry.init({
      dsn: this.config.sentry.dsn,
      environment: this.config.sentry.environment,
      release: this.config.sentry.release,
      tracesSampleRate: this.config.sentry.tracesSampleRate ?? 0.1,
      beforeSend: event => sanitizeSentryEvent(event) as Sentry.ErrorEvent,
      ignoreErrors: [
        'ResizeObserver loop limit exceeded',
        'Network request failed',
        'Load failed',
        'ChunkLoadError',
      ],
    });

    this.initialized = true;
  }

  // ─────────────────────────────────────────────
  // LOGS
  // ─────────────────────────────────────────────

  log(level: LogLevel, message: string, context?: LogContext): void {
    const logData = { level, message, timestamp: new Date().toISOString(), ...context };
    const consoleMethod = level === 'error' ? 'error' : level === 'warn' ? 'warn' : 'log';
    console[consoleMethod](JSON.stringify(logData));

    Sentry.addBreadcrumb({ message, level: level as Sentry.SeverityLevel, data: context });
  }

  // ─────────────────────────────────────────────
  // ERRORS
  // ─────────────────────────────────────────────

  captureError(error: Error, context?: LogContext): void {
    Sentry.captureException(error, { extra: context });
  }

  // ─────────────────────────────────────────────
  // ANALYTICS — fan-out a todos los adapters
  // ─────────────────────────────────────────────

  track(event: AnalyticsEvent): void {
    // Breadcrumb en Sentry para correlacionar con errores
    Sentry.addBreadcrumb({
      category: 'analytics',
      message: event.name,
      data: event.properties,
      level: 'info',
    });

    // Fan-out a todos los adapters (GTM, Amplitude, etc.)
    this.adapters.forEach(adapter => adapter.track(event));

    if (this.config.analytics?.debug) {
      logger.info('[Analytics]', event.name, event.properties);
    }
  }

  identify(userId: string, traits?: Record<string, unknown>): void {
    Sentry.setUser({ id: userId });
    this.adapters.forEach(adapter => adapter.identify?.(userId, traits));

    if (this.config.analytics?.debug) {
      logger.info('[Analytics] Identify', userId, traits);
    }
  }

  // ─────────────────────────────────────────────
  // PERFORMANCE
  // ─────────────────────────────────────────────

  startSpan(name: string, attributes?: Record<string, unknown>): Span {
    return { name, startTime: performance.now(), attributes };
  }

  endSpan(span: Span): void {
    span.endTime = performance.now();
    this.track({
      name: 'performance_span',
      properties: {
        spanName: span.name,
        durationMs: span.endTime - span.startTime,
        ...span.attributes,
      },
    });
  }

  // ─────────────────────────────────────────────
  // USER CONTEXT
  // ─────────────────────────────────────────────

  setUser(user: { id: string; email?: string } | null): void {
    Sentry.setUser(user ? { id: user.id, email: user.email } : null);
    this.adapters.forEach(adapter => adapter.setUser?.(user));
  }
}

// ═══════════════════════════════════════════════════════════════
// REACT CONTEXT
// ═══════════════════════════════════════════════════════════════

export const TelemetryContext = createContext<TelemetryProvider | null>(null);

interface TelemetryProviderProps {
  config: TelemetryConfig;
  adapters?: AnalyticsAdapter[];
  children: ReactNode;
}

export function TelemetryProviderComponent({
  config,
  adapters = [],
  children,
}: TelemetryProviderProps) {
  const provider = useMemo(
    () => new WebTelemetryProvider(config, adapters),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return <TelemetryContext.Provider value={provider}>{children}</TelemetryContext.Provider>;
}
